/**
 * Coding walkthrough.
 *
 * Walks the user through every uncoded vendor in the current batch,
 * letting them assign GL/property codes and (optionally) save the
 * decision as a rule for future invoices.
 *
 * Vendors are deduped: multiple invoices from the same vendor are
 * coded together in one decision.
 */

import { el, toast, openCodingModal } from './dom.js';
import { getState, setState, updateFile } from '../state.js';
import { matchVendor, saveRule, applyRulesToFiles, fileIsUncoded } from '../lib/rules.js';
import { saveBatchSnapshot } from '../persistence.js';

/**
 * Start the walkthrough. Calls onDone when finished.
 */
export async function startCodingWalkthrough(onDone) {
  const groups = collectUncodedGroups(getState().files);
  if (groups.length === 0) {
    toast('No uncoded vendors. Everything is ready to export.', 'success');
    if (onDone) onDone();
    return;
  }

  let i = 0;
  const next = () => {
    if (i >= groups.length) {
      saveBatchSnapshot().catch(() => {});
      toast('Coding complete.', 'success');
      if (onDone) onDone();
      return;
    }
    showOne(groups[i], i, groups.length, () => { i++; next(); });
  };
  next();
}

/* -------- Group uncoded vendors -------- */

function collectUncodedGroups(files) {
  const map = new Map(); // normalizedName → { vendorName, sample, fileIds[] }
  for (const f of files) {
    if (f.status !== 'done' || !f.extracted) continue;
    if (!fileIsUncoded(f)) continue;
    const v = f.extracted.vendor_name || '(unknown vendor)';
    const key = v.trim().toLowerCase();
    if (!map.has(key)) {
      map.set(key, { vendorName: v, sample: f, fileIds: [] });
    }
    map.get(key).fileIds.push(f.id);
  }
  return [...map.values()];
}

/* -------- Single vendor decision UI -------- */

function showOne(group, idx, total, onAdvance) {
  const { vendorName, sample, fileIds } = group;
  const e = sample.extracted || {};

  const glInput   = el('input', { type: 'text', class: 'input-mono', placeholder: 'e.g. 6200-REPAIRS' });
  const propInput = el('input', { type: 'text', class: 'input-mono', placeholder: 'e.g. HOU-001' });
  const noteInput = el('input', { type: 'text', placeholder: '(optional) note for the rule' });
  const saveRuleCheckbox = el('input', { type: 'checkbox', id: 'save-rule', checked: true });

  // If a rule already exists at a lower threshold, prefill
  matchVendor(vendorName, 'normalized').then(m => {
    if (m) {
      glInput.value   = m.rule.glCodeDefault   || '';
      propInput.value = m.rule.propertyCodeDefault || '';
      noteInput.value = m.rule.note     || '';
    }
  }).catch(() => {});

  const invoiceMeta = [
    e.invoice_number && `Invoice ${e.invoice_number}`,
    e.invoice_date,
    typeof e.total_amount === 'number'
      && new Intl.NumberFormat('en-US', { style: 'currency', currency: e.currency || 'USD' }).format(e.total_amount),
    fileIds.length > 1 && `${fileIds.length} invoices in this batch`
  ].filter(Boolean).join(' · ');

  const body = el('div', { class: 'form-row' }, [
    el('div', { class: 'field-group' }, [
      el('label', {}, ['GL code']),
      glInput
    ]),
    el('div', { class: 'field-group' }, [
      el('label', {}, ['Property code']),
      propInput
    ]),
    el('div', { class: 'field-group' }, [
      el('label', {}, ['Note (optional)']),
      noteInput
    ]),
    el('div', { class: 'toggle-row' }, [
      saveRuleCheckbox,
      el('label', { for: 'save-rule' }, [
        el('div', { class: 'toggle-title' }, [`Remember this for "${vendorName}"`]),
        el('div', { class: 'toggle-help' }, [
          'Save these codes as a rule. Future invoices from this vendor will be coded automatically.'
        ])
      ])
    ])
  ]);

  // Auto-focus first field
  setTimeout(() => glInput.focus(), 100);

  // Submit on Enter from any field
  for (const inp of [glInput, propInput, noteInput]) {
    inp.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); applyAndAdvance(); }
    });
  }

  const closeRef = { close: null };
  const applyAndAdvance = async () => {
    const gl = glInput.value.trim();
    const pr = propInput.value.trim();
    const note = noteInput.value.trim();
    if (!gl && !pr) {
      toast('Enter at least a GL code or property code.', 'error');
      return false;
    }

    // Apply to all matching files in the batch
    const state = getState();
    for (const fid of fileIds) {
      updateFile(fid, {
        coding: { glCode: gl, propertyCode: pr, source: 'manual' }
      });
    }

    // Optionally save as a rule
    if (saveRuleCheckbox.checked) {
      try {
        await saveRule({
          vendorMatch: vendorName,
          glCodeDefault: gl,
          propertyCodeDefault: pr,
          taxGl: gl,
          emitDistributionLines: false,
          lineItemRules: [],
          note
        });
        // Re-apply so files get tagged with source: 'rule' going forward
        await applyRulesToFiles(state.files, state.settings.matchStrategy, state.settings.matchThreshold);
        // Restore manual flag for the just-coded files (rule + manual is a wash;
        // but applyRulesToFiles preserves manual, so they stay manual)
        setState({ files: state.files });
      } catch (err) {
        toast(`Could not save rule: ${err.message}`, 'error');
      }
    }

    saveBatchSnapshot().catch(() => {});
    if (closeRef.close) closeRef.close();
    onAdvance();
    return true;
  };

  closeRef.close = openCodingModal({
    eyebrow: `Vendor ${idx + 1} of ${total}`,
    vendorName,
    invoiceMeta,
    body,
    primaryLabel: 'Apply codes',
    onPrimary: () => { applyAndAdvance(); return false; },  // we handle close manually
    onSkip: (close) => { close(); onAdvance(); }
  });
}
